import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { Work } from "@/types";

interface HeroCarouselProps {
  items: Work[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef(0);
  const timer = useRef<number | null>(null);

  const go = (next: number) => {
    setIndex((next + items.length) % items.length);
  };

  const resetTimer = () => {
    if (timer.current) window.clearInterval(timer.current);
    timer.current = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % items.length);
    }, 6000);
  };

  useEffect(() => {
    if (items.length <= 1) return;
    resetTimer();
    return () => {
      if (timer.current) window.clearInterval(timer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length]);

  if (items.length === 0) return null;

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) {
      go(delta > 0 ? index - 1 : index + 1);
      resetTimer();
    }
  };

  return (
    <section className="relative w-full h-[560px] md:h-[680px] lg:h-[100vh] lg:min-h-[640px] overflow-hidden">
      {items.map((item, i) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-[1200ms] ${
            i === index ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={item.cover}
            alt={`${item.title} - 女性ins大片焦点作品`}
            title={`${item.title} - 时尚女性摄影作品`}
            className={`w-full h-full object-cover object-top ${
              i === index ? "animate-slow-zoom" : ""
            }`}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/70 via-foreground-950/35 to-foreground-950/10" />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/60 via-transparent to-foreground-950/30" />
        </div>
      ))}

      <div
        className="absolute inset-0 flex items-center"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <div className="w-full max-w-7xl mx-auto px-4 md:px-8">
          <div key={items[index].id} className="max-w-2xl animate-fade-up">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-500/20 border border-accent-400/40 text-accent-300 text-xs font-medium mb-5">
              <i className="ri-fire-line"></i>
              本期焦点 · {items[index].categoryName}
            </span>
            <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl font-bold text-background-50 leading-tight mb-5">
              {items[index].title}
            </h1>
            <p className="text-background-100/85 text-sm md:text-base leading-relaxed max-w-xl mb-8 line-clamp-2">
              {items[index].description}
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${items[index].id}`}
                className="px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
              >
                欣赏完整作品
                <i className="ri-arrow-right-line"></i>
              </Link>
              <Link
                to={`/category/${items[index].categoryId}`}
                className="px-6 py-3 rounded-md border border-background-50/50 text-background-50 text-sm font-medium hover:bg-background-50/10 transition-colors cursor-pointer whitespace-nowrap"
              >
                浏览同分类
              </Link>
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        aria-label="上一张"
        onClick={() => {
          go(index - 1);
          resetTimer();
        }}
        className="hidden md:flex absolute left-5 lg:left-8 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full border border-background-50/40 text-background-50 items-center justify-center hover:bg-background-50/15 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-xl"></i>
      </button>
      <button
        type="button"
        aria-label="下一张"
        onClick={() => {
          go(index + 1);
          resetTimer();
        }}
        className="hidden md:flex absolute right-5 lg:right-8 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full border border-background-50/40 text-background-50 items-center justify-center hover:bg-background-50/15 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-xl"></i>
      </button>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2.5">
        {items.map((item, i) => (
          <button
            key={item.id}
            type="button"
            aria-label={`切换到第 ${i + 1} 张`}
            onClick={() => {
              go(i);
              resetTimer();
            }}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              i === index ? "w-8 bg-accent-500" : "w-3 bg-background-50/50"
            }`}
          />
        ))}
      </div>
    </section>
  );
}